import type {
  EditableThemeTokensByVariant,
  ThemeMode,
  ThemePresetId,
  ThemeVariant,
} from '@/shared/theme/theme-config'
import {
  THEME_MODE_STORAGE_KEY,
  THEME_PRESET_STORAGE_KEY,
  resolveThemePreset,
} from '@/shared/theme/theme-config'

export function getStoredMode() {
  const storedMode = localStorage.getItem(
    THEME_MODE_STORAGE_KEY,
  ) as ThemeMode | null

  return storedMode ?? 'system'
}

export function getStoredPreset(): ThemePresetId {
  const storedPreset = localStorage.getItem(THEME_PRESET_STORAGE_KEY)

  if (storedPreset === 'custom') {
    return 'current'
  }

  return (storedPreset ?? 'current') as ThemePresetId
}

export function resolveEffectiveMode(mode: ThemeMode): ThemeVariant {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
  }

  return mode
}

export function applyTheme({
  mode,
  preset,
  workspaceTokens,
}: {
  mode: ThemeMode
  preset: ThemePresetId
  workspaceTokens: EditableThemeTokensByVariant | null
}) {
  const effectiveMode = resolveEffectiveMode(mode)
  const resolvedPreset = resolveThemePreset({ preset, workspaceTokens })
  const resolvedTokens = resolvedPreset[effectiveMode]

  if (effectiveMode === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }

  for (const [tokenName, tokenValue] of Object.entries(resolvedTokens)) {
    document.documentElement.style.setProperty(`--${tokenName}`, tokenValue)
  }
}

export function persistThemeState({
  mode,
  preset,
}: {
  mode: ThemeMode
  preset: ThemePresetId
}) {
  localStorage.setItem(THEME_MODE_STORAGE_KEY, mode)
  localStorage.setItem(THEME_PRESET_STORAGE_KEY, preset)
}
